// 📄 C:\Users\Caner\inosel-panel\ortak\src\fatura-takip\src\pages\FaturaListesi.js
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, addDoc, serverTimestamp, query, where } from "firebase/firestore";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "dayjs/locale/tr";
dayjs.locale("tr");

const alanAdlari = {
  faturaNo: "Fatura No",
  firmaAdi: "Firma",
  plaka: "Plaka",
  createdBy: "Oluşturan",
  aciklama: "Açıklama",
  tarih: "Fatura Tarihi",
  urunAdi: "Ürün Adı",
  fiyat: "Fiyat",
  adet: "Adet",
  grup: "Grup",
  altGrup: "Alt Grup",
  kategori: "Kategori",
  satirTutar: "Tutar (KDV'siz)",
  kdvTutar: "KDV Tutarı",
  iskonto: "İskonto %",
  iskontoTutar: "İskonto Tutarı",
  netTutar: "Net Tutar",
  kdvDahilTutar: "Tutar (KDV Dahil)",
  tesis: "Tesis",
  aracCinsi: "Araç Cinsi",
  ay: "Ay",
  kdv: "KDV %"
};

const sayisalAlanlar = [
  "fiyat",
  "adet",
  "satirTutar",
  "kdvTutar",
  "kdvDahilTutar",
  "iskontoTutar",
  "netTutar"
];

const parseExcelDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const parts = String(value).split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts.map(Number);
    const date = new Date(year, month - 1, day);
    if (!Number.isNaN(date.getTime())) return date;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
};

// 🔧 tarih ön ayarları (dinamik)
const DATE_PRESETS = [
  { value: "BU_AY", label: "Bu Ay" },
  { value: "SON_7", label: "Son 7 Gün" },
  { value: "GECEN_AY", label: "Geçen Ay" },
  { value: "BU_YIL", label: "Bu Yıl" },
  { value: "GECEN_YIL", label: "Geçen Yıl" },
  { value: "OZEL", label: "Özel" }
];

const FaturaListesi = () => {
  const [faturalar, setFaturalar] = useState([]);
  const [columns, setColumns] = useState(["grup", "tesis", "plaka", "urunAdi", "netTutar"]);
  const [baslangic, setBaslangic] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [bitis, setBitis] = useState(dayjs().format("YYYY-MM-DD"));
  const [tumAlanlar, setTumAlanlar] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterDropdowns, setFilterDropdowns] = useState({});
  const dropdownRefs = useRef({});
  const [columnWidths, setColumnWidths] = useState({});
  const [sortedBy, setSortedBy] = useState({ col: null, asc: true }); // Nesneye çevirdik
  const [filterSearch, setFilterSearch] = useState(""); // Arama için ekledik

  // 🎯 Rapor Tasarımları (herkese açık)
  const [designs, setDesigns] = useState([]);
  const [selectedDesignId, setSelectedDesignId] = useState("");
  const [designName, setDesignName] = useState("");
  const [datePreset, setDatePreset] = useState("BU_AY"); // varsayılan

  // ➕ Ara Toplam
  const [subtotalBy, setSubtotalBy] = useState(""); // örn: "plaka"
  const subtotalCandidates = useMemo(
    () => columns.filter((c) => !sayisalAlanlar.includes(c)),
    [columns]
  );

// -- DATA FETCH --
useEffect(() => {
  let isMounted = true;
  (async () => {
    // Tasarımlar için query (sorgu) oluşturuyoruz
    const designsQuery = query(
      collection(db, "reportDesigns"),
      where("module", "==", "fatura") // Sadece fatura tasarımlarını getir
    );

    const [faturaSnap, categorySnap, plateSnap, designSnap] = await Promise.all([
      getDocs(collection(db, "faturalar")),
      getDocs(collection(db, "categories")),
      getDocs(collection(db, "plates")),
      getDocs(designsQuery) // Filtrelenmiş sorguyu buraya koyduk
    ]);
    
    // ... geri kalan kodlar aynı

      if (!isMounted) return;

      setDesigns(
        designSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"))
      );

      const kategoriler = categorySnap.docs.map(doc => doc.data());
      const plakaTesisMap = Object.fromEntries(
        plateSnap.docs.map(doc => [doc.data().plate, doc.data().tesis || ""])
      );
      const plakaAracMap = Object.fromEntries(
        plateSnap.docs.map(doc => [doc.data().plate, doc.data().aracCinsi || ""])
      );

      const veriler = faturaSnap.docs.flatMap(doc => {
        const data = doc.data();
        if (data.deleted) return [];
        const tarihObj = data.tarih?.toDate?.();
        return (data.satirlar || []).map(satir => {
          const match = kategoriler.find(k => k.icerik?.includes(satir.urunAdi));
          const adet = parseFloat(satir.adet ?? 1);
          const fiyat = parseFloat(satir.fiyat ?? 0);
          const iskonto = parseFloat(satir.iskonto || 0);
          const kdv = parseFloat(satir.kdv || 0);
          const tutar = fiyat * adet;
          const iskontoTutar = tutar * (iskonto / 100);
          const netTutar = tutar - iskontoTutar;

          return {
            ...data,
            ...satir,
            tarih: tarihObj ? dayjs(tarihObj).format("YYYY-MM-DD") : "",
            grup: match?.grup || "",
            altGrup: match?.altGrup || "",
            kategori: match?.kategori || "",
            adet,
            satirTutar: tutar,
            iskonto,
            iskontoTutar,
            netTutar,
            kdv,
            kdvTutar: netTutar * (kdv / 100),
            kdvDahilTutar: netTutar * (1 + kdv / 100),
            tesis: satir.tesis || plakaTesisMap[satir.plaka] || "",
            aracCinsi: plakaAracMap[satir.plaka] || "",
            ay: tarihObj ? dayjs(tarihObj).format("MMMM") : ""
          };
        });
      });

      setFaturalar(veriler);
      setTumAlanlar(Object.keys(alanAdlari).filter(k => veriler.some(v => k in v)));
    })();
    return () => { isMounted = false; };
  }, []);

  // Dışarı tıklamada popup kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.entries(dropdownRefs.current).forEach(([col, ref]) => {
        if (ref && !ref.contains(event.target)) {
          setFilterDropdowns(prev => ({ ...prev, [col]: false }));
        }
      });
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 📌 Alanlar: alfabetik sırala (etikete göre)
  const sortedTumAlanlar = useMemo(
    () => [...tumAlanlar].sort((a, b) => (alanAdlari[a] || a).localeCompare(alanAdlari[b] || b, "tr")),
    [tumAlanlar]
  );

  // 📌 Filtre popup (okun dibinde)
const toggleDropdown = (col, e) => {
    e.stopPropagation();
    setFilterSearch(""); // 👈 Her açıldığında arama kutusunu sıfırlar
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterDropdowns(prev => ({
      ...prev,
      [col]: prev[col] ? false : { top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 160) }
    }));
  };

  const handleFilterChange = (col, val) => {
    setFilters(prev => {
      const mevcut = prev[col] || [];
      return mevcut.includes(val)
        ? { ...prev, [col]: mevcut.filter(v => v !== val) }
        : { ...prev, [col]: [...mevcut, val] };
    });
  };

  // ⏱ tarih ön ayar uygulama — dinamik
  const applyDatePreset = (preset) => {
    const today = dayjs();
    if (preset === "BU_AY") {
      setBaslangic(today.startOf("month").format("YYYY-MM-DD"));
      setBitis(today.format("YYYY-MM-DD"));
    } else if (preset === "SON_7") {
      setBaslangic(today.subtract(6, "day").format("YYYY-MM-DD"));
      setBitis(today.format("YYYY-MM-DD"));
    } else if (preset === "GECEN_AY") {
      const lastMonth = today.subtract(1, "month");
      setBaslangic(lastMonth.startOf("month").format("YYYY-MM-DD"));
      setBitis(lastMonth.endOf("month").format("YYYY-MM-DD"));
    } else if (preset === "BU_YIL") {
      setBaslangic(today.startOf("year").format("YYYY-MM-DD"));
      setBitis(today.format("YYYY-MM-DD"));
    } else if (preset === "GECEN_YIL") {
      const lastYear = today.subtract(1, "year");
      setBaslangic(lastYear.startOf("year").format("YYYY-MM-DD"));
      setBitis(lastYear.endOf("year").format("YYYY-MM-DD"));
    }
    setDatePreset(preset);
  };

  // Filtreleme
  const filtreliFaturalar = useMemo(() => {
    return faturalar.filter(f =>
      Object.entries(filters).every(([col, values]) => !values.length || values.includes(f[col])) &&
      (!baslangic || f.tarih >= baslangic) &&
      (!bitis || f.tarih <= bitis)
    );
  }, [faturalar, filters, baslangic, bitis]);

const sortedFaturalar = useMemo(() => {
    if (!sortedBy || !sortedBy.col) return filtreliFaturalar;
    
    return [...filtreliFaturalar].sort((a, b) => {
      let valA = a[sortedBy.col] || "";
      let valB = b[sortedBy.col] || "";

      // Eğer kolon sayısal bir alansa sayısal sıralama yap
      if (sayisalAlanlar.includes(sortedBy.col)) {
        valA = parseFloat(valA) || 0;
        valB = parseFloat(valB) || 0;
        return sortedBy.asc ? valA - valB : valB - valA;
      }

      // Metinsel (String) sıralama
      const res = String(valA).localeCompare(String(valB), "tr");
      return sortedBy.asc ? res : -res;
    });
  }, [filtreliFaturalar, sortedBy]);

  // 🔢 PİVOT: Sayısal alanları sayı ile başlat/sayısal topla
  const { liste, toplamlar } = useMemo(() => {
    const pivotVeri = Object.create(null);

    for (let i = 0; i < sortedFaturalar.length; i++) {
      const row = sortedFaturalar[i];

      const key = columns
        .filter(c => !sayisalAlanlar.includes(c))
        .map(c => row[c])
        .join("||");

      if (!pivotVeri[key]) {
        const obj = {};
        for (let j = 0; j < columns.length; j++) {
          const c = columns[j];
          if (sayisalAlanlar.includes(c)) {
            const ilk = parseFloat(row[c] ?? 0);
            obj[c] = isNaN(ilk) ? 0 : ilk;
          } else {
            obj[c] = row[c] ?? "";
          }
        }
        pivotVeri[key] = obj;
      } else {
        for (let j = 0; j < sayisalAlanlar.length; j++) {
          const c = sayisalAlanlar[j];
          if (columns.includes(c)) {
            const mevcut = parseFloat(pivotVeri[key][c] ?? 0) || 0;
            const eklenecek = parseFloat(row[c] ?? 0) || 0;
            pivotVeri[key][c] = mevcut + eklenecek;
          }
        }
      }
    }

    const listeLocal = Object.values(pivotVeri);
    const toplamlarLocal = {};
    for (let j = 0; j < columns.length; j++) {
      const c = columns[j];
      if (sayisalAlanlar.includes(c)) {
        let sum = 0;
        for (let k = 0; k < listeLocal.length; k++) {
          const val = parseFloat(listeLocal[k][c]) || 0;
          sum += val;
        }
        toplamlarLocal[c] = sum;
      }
    }

    return { liste: listeLocal, toplamlar: toplamlarLocal };
  }, [sortedFaturalar, columns]);

  // 📊 Ara Toplamlı render listesi (sadece görüntü için; export’ta ham listeyi kullanacağız)
  const listeAraToplamli = useMemo(() => {
    if (!subtotalBy || !columns.includes(subtotalBy)) return liste;

    // önce o kolona göre sırala (metin)
    const arr = [...liste].sort((a, b) => (a[subtotalBy] || "").localeCompare(b[subtotalBy] || "", "tr"));

    const cikan = [];
    let current = null;
    let acc = {}; // sayısal toplayıcı

    const pushSubtotal = () => {
      if (current === null) return;
      const subtotalRow = {};
      columns.forEach((c) => {
        if (sayisalAlanlar.includes(c)) {
          subtotalRow[c] = acc[c] || 0;
        } else if (c === subtotalBy) {
          subtotalRow[c] = `— Ara Toplam (${alanAdlari[subtotalBy]}: ${current}) —`;
        } else {
          subtotalRow[c] = "";
        }
      });
      cikan.push({ ...subtotalRow, __subtotal: true });
      acc = {};
    };

    for (let i = 0; i < arr.length; i++) {
      const row = arr[i];
      const val = row[subtotalBy] ?? "";

      if (current === null) current = val;
      if (val !== current) {
        pushSubtotal();
        current = val;
      }

      // akümüle et
      sayisalAlanlar.forEach((c) => {
        if (columns.includes(c)) {
          const prev = acc[c] || 0;
          acc[c] = prev + (parseFloat(row[c]) || 0);
        }
      });

      cikan.push(row);
    }
    // son grup
    pushSubtotal();

    return cikan;
  }, [liste, columns, subtotalBy]);

  // Excel (sayıyı sayı olarak yaz): subtotal satırlarını da dahil ediyorum
  const exportToExcel = () => {
    // export sırasında sayısal kolonlara **ham sayılar**; metin kolonlara string veriyoruz.
    const exportRows = (subtotalBy ? listeAraToplamli : liste).map((row) => {
      const out = {};
      columns.forEach((col) => {
        if (row.__subtotal) {
          // ara toplam satırları: metin kolonları label, sayısallar sayı
          if (sayisalAlanlar.includes(col)) {
            out[alanAdlari[col] || col] = Number(row[col] || 0);
          } else if (col === "tarih") {
            out[alanAdlari[col] || col] = parseExcelDate(row[col]);
          } else {
            out[alanAdlari[col] || col] = row[col] || "";
          }
        } else {
          // normal satır
          if (sayisalAlanlar.includes(col)) {
            out[alanAdlari[col] || col] = Number(row[col] || 0);
          } else if (col === "tarih") {
            out[alanAdlari[col] || col] = parseExcelDate(row[col]);
          } else {
            out[alanAdlari[col] || col] = row[col] || "";
          }
        }
      });
      return out;
    });

    const ws = XLSX.utils.json_to_sheet(exportRows, { cellDates: true });
    const tarihColIndex = columns.indexOf("tarih");
    if (tarihColIndex >= 0) {
      const tarihColLetter = XLSX.utils.encode_col(tarihColIndex);
      for (let rowIndex = 1; rowIndex <= exportRows.length; rowIndex++) {
        const cell = ws[`${tarihColLetter}${rowIndex + 1}`];
        if (cell && cell.v) {
          cell.t = "d";
          cell.z = "dd.mm.yyyy";
        }
      }
    }
    // (İsteğe bağlı) sayı hücrelerine biçim verilebilir; şimdilik ham sayı bırakalım.
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FaturaListesi");
    XLSX.writeFile(wb, "fatura_listesi.xlsx");
  };

  const formatSayi = (deger, tip) => {
    if (deger === undefined || deger === null || isNaN(deger)) return "";
    if (tip === "adet") {
      return Number(deger).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    const formatted = Number(deger).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return ["fiyat", "satirTutar", "kdvTutar", "netTutar", "kdvDahilTutar", "iskontoTutar"].includes(tip) ? `${formatted} ₺` : formatted;
  };

  const getFilteredOptions = (col) => {
    const otherFilters = Object.fromEntries(Object.entries(filters).filter(([k]) => k !== col));
    const filtered = faturalar.filter(f =>
      Object.entries(otherFilters).every(([key, vals]) => !vals.length || vals.includes(f[key])) &&
      (!baslangic || f.tarih >= baslangic) &&
      (!bitis || f.tarih <= bitis)
    );
    return [...new Set(filtered.map(f => f[col] || ""))].sort();
  };

  // 💾 Tasarım kaydet (herkese görünür)
const handleSaveDesign = async () => {
  // Buradaki 'reportName' senin input'a bağlı olan state adın olmalı
  // Eğer state adın farklıysa (örn: designName), burayı ona göre güncelle.
  if (!designName || !designName.trim()) return alert("Lütfen bir rapor adı girin!");

  const payload = {
    name: designName.trim(),
    module: "fatura", // Modül mühürleme buraya eklendi
    columns: columns,
    datePreset: datePreset,
    baslangic: baslangic,
    bitis: bitis,
    ownerEmail: auth.currentUser?.email,
    createdAt: serverTimestamp()
  };

  try {
    const ref = await addDoc(collection(db, "reportDesigns"), payload);
    setDesigns(prev =>
      [...prev, { id: ref.id, ...payload }].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"))
    );
    setSelectedDesignId(ref.id);
    alert("Rapor tasarımı kaydedildi ✅");
    // Kayıttan sonra inputu temizlemek istersen:
    // setReportName(""); 
  } catch (error) {
    console.error("Kaydetme hatası:", error);
  }
};

  // 📥 Tasarım uygula (preset dinamik)
  const applyDesign = (d) => {
    if (!d) return;
    const cols = (d.columns || []).filter(c => alanAdlari[c]);
    if (cols.length) setColumns(cols);

    if (d.datePreset && d.datePreset !== "OZEL") {
      applyDatePreset(d.datePreset); // 👈 her açışta “bugün”e göre hesaplar
    } else {
      setDatePreset("OZEL");
      if (d.baslangic) setBaslangic(d.baslangic);
      if (d.bitis) setBitis(d.bitis);
    }
  };

  return (
    <div style={{ padding: 10, position: "relative" }}>
      {/* Üst Bar: Alanlar + Tarih + Tasarım + Ara Toplam */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div>
            <h4 style={{ fontSize: "12px", marginBottom: 5 }}>Alanlar:</h4>
            <div style={{ border: "1px solid #ccc", borderRadius: 4, padding: 5, width: 200, height: 150, overflowY: "auto", fontSize: "11px" }}>
              {sortedTumAlanlar.map((alan) => (
                <label key={alan} style={{ display: "block", backgroundColor: filters[alan]?.length ? "#dbeafe" : "" }}>
                  <input
                    type="checkbox"
                    checked={columns.includes(alan)}
                    onChange={(e) =>
                      setColumns((prev) =>
                        e.target.checked ? [...prev, alan] : prev.filter((c) => c !== alan)
                      )
                    }
                  /> {alanAdlari[alan] || alan}
                  {!sayisalAlanlar.includes(alan) && (
                    <button
                      onClick={(ev) => toggleDropdown(alan, ev)}
                      style={{
                        marginLeft: 6,
                        fontSize: "10px",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        backgroundColor: filters[alan]?.length ? "#bfdbfe" : "white",
                        cursor: "pointer"
                      }}
                      title="Filtrele"
                    >
                      ▼
                    </button>
                  )}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: "12px", marginBottom: 5 }}>Tarih:</h4>
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
              <select
                value={datePreset}
                onChange={(e) => applyDatePreset(e.target.value)}
                style={{ height: 26, fontSize: 12 }}
              >
                {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)} style={{ width: 140 }} />
              <input type="date" value={bitis} onChange={(e) => setBitis(e.target.value)} style={{ width: 140 }} />
            </div>

            {/* Tasarım kaydet/yükle */}
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input
                placeholder="Tasarım adı (örn: Aylık Grup Harcama)"
                value={designName}
                onChange={(e) => setDesignName(e.target.value)}
                style={{ width: 240, height: 26, fontSize: 12, padding: "0 6px", border: "1px solid #ccc", borderRadius: 4 }}
              />
              <button
                onClick={handleSaveDesign}
                style={{ background: "#2563eb", color: "white", fontSize: "12px", padding: "4px 8px", borderRadius: 4, border: "none", cursor: "pointer", height: 26 }}
              >
                Kaydet
              </button>
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
              <select
                value={selectedDesignId}
                onChange={(e) => {
                  const d = designs.find(x => x.id === e.target.value);
                  setSelectedDesignId(e.target.value);
                  applyDesign(d);
                }}
                style={{ width: 380, height: 26, fontSize: 12 }}
              >
                <option value="">(Kayıtlı tasarım seç)</option>
                {designs.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name} {d.ownerEmail ? `— ${d.ownerEmail}` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Ara Toplam */}
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12, marginRight: 6 }}>Ara Toplam Alanı:</label>
              <select
                value={subtotalBy}
                onChange={(e) => setSubtotalBy(e.target.value)}
                style={{ height: 26, fontSize: 12 }}
              >
                <option value="">(Yok)</option>
                {subtotalCandidates.map((c) => (
                  <option key={c} value={c}>{alanAdlari[c] || c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <button onClick={exportToExcel} style={{ background: "#22c55e", color: "white", fontSize: "12px", padding: "6px 12px", borderRadius: 4, border: "none", cursor: "pointer", height: 30 }}>
          Excel'e Aktar
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "11px", tableLayout: "fixed", width: "100%" }}>
          <thead style={{ background: "#f1f5f9" }}>
            <tr>
              {columns.map((col) => (
                <th key={col} style={{ border: "1px solid #ccc", padding: 5, textAlign: "left", position: "relative", minWidth: columnWidths[col] || 120 }}>
                  {alanAdlari[col] || col}
                  {!sayisalAlanlar.includes(col) && (
                    <button onClick={(e) => toggleDropdown(col, e)} style={{ marginLeft: 5, fontSize: "10px", border: "1px solid #ccc", borderRadius: 4, backgroundColor: filters[col]?.length ? "#bfdbfe" : "white", cursor: "pointer" }}>▼</button>
                  )}
                  <div
                    onMouseDown={(e) => {
                      const startX = e.clientX;
                      const startWidth = columnWidths[col] || 120;
                      const onMouseMove = (moveEvent) => {
                        const newWidth = startWidth + (moveEvent.clientX - startX);
                        setColumnWidths((prev) => ({ ...prev, [col]: newWidth }));
                      };
                      const onMouseUp = () => {
                        window.removeEventListener("mousemove", onMouseMove);
                        window.removeEventListener("mouseup", onMouseUp);
                      };
                      window.addEventListener("mousemove", onMouseMove);
                      window.addEventListener("mouseup", onMouseUp);
                    }}
                    style={{ cursor: "col-resize", position: "absolute", top: 0, right: 0, width: 5, height: "100%" }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Genel toplam satırı */}
            <tr style={{ background: "#fef9c3", fontWeight: "bold" }}>
              {columns.map((col) => (
                <td key={col} style={{ border: "1px solid #ccc", padding: 5 }}>
                  {toplamlar[col] !== undefined ? formatSayi(toplamlar[col], col) : ""}
                </td>
              ))}
            </tr>

            {/* Liste + Ara Toplam satırları */}
            {(subtotalBy ? listeAraToplamli : liste).map((row, i) => {
              const isSubtotal = row.__subtotal;
              return (
                <tr key={i} style={isSubtotal ? { background: "#f3f4f6", fontWeight: "bold" } : undefined}>
                  {columns.map((col) => (
                    <td key={col} style={{ border: "1px solid #ccc", padding: 5 }}>
                      {sayisalAlanlar.includes(col)
                        ? formatSayi(row[col], col)
                        : (row[col] || "")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Filtre popup'ları: okun hemen dibinde */}
{Object.entries(filterDropdowns).map(([col, pos]) => (
        pos && (
          <div
            key={col}
            ref={(el) => (dropdownRefs.current[col] = el)}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              background: "white",
              border: "1px solid #ccc",
              borderRadius: 4,
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              zIndex: 9999,
              fontSize: "11px",
              width: 200, // Genişliği biraz artırdık
              padding: 8,
              maxHeight: 260,
              overflowY: "auto"
            }}
          >
            {/* 1. SIRALAMA BUTONU (Her basışta yön değiştirir) */}
            <button 
              onClick={() => setSortedBy(prev => ({ col: col, asc: prev?.col === col ? !prev.asc : true }))} 
              style={{ width: "100%", fontSize: "11px", marginBottom: 8, cursor: "pointer", padding: "4px" }}
            >
              {sortedBy?.col === col && !sortedBy?.asc ? "↑ A'dan Z'ye Sırala" : "↓ Z'den A'ya Sırala"}
            </button>

            {/* 2. ARAMA KUTUSU */}
            <input
              placeholder="Ara..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              style={{ width: "100%", marginBottom: 8, padding: "4px", fontSize: "11px", boxSizing: "border-box" }}
            />

            {/* 3. TÜMÜNÜ SEÇ / KALDIR (Aramaya göre dinamik) */}
            <label style={{ display: "block", marginBottom: 6, fontWeight: "bold", borderBottom: "1px solid #eee", paddingBottom: "4px" }}>
              <input
                type="checkbox"
                checked={
                  getFilteredOptions(col).filter(v => (v || "").toString().toLowerCase().includes(filterSearch.toLowerCase())).length > 0 &&
                  getFilteredOptions(col).filter(v => (v || "").toString().toLowerCase().includes(filterSearch.toLowerCase())).every(v => filters[col]?.includes(v))
                }
                onChange={(e) => {
                  const visibleOptions = getFilteredOptions(col).filter(v => (v || "").toString().toLowerCase().includes(filterSearch.toLowerCase()));
                  setFilters((prev) => ({
                    ...prev,
                    [col]: e.target.checked ? Array.from(new Set([...(prev[col] || []), ...visibleOptions])) : (prev[col] || []).filter(v => !visibleOptions.includes(v))
                  }));
                }}
              /> Tümünü seç / kaldır
            </label>

            {/* 4. SEÇENEKLER LİSTESİ (Filtreli ve Sıralı) */}
            {getFilteredOptions(col)
              .filter(val => (val || "").toString().toLowerCase().includes(filterSearch.toLowerCase()))
              .sort((a, b) => {
                const res = String(a).localeCompare(String(b), "tr");
                return sortedBy?.asc ? res : -res;
              })
              .map((val) => (
                <label key={val} style={{ display: "block", marginBottom: 2 }}>
                  <input
                    type="checkbox"
                    checked={filters[col]?.includes(val) || false}
                    onChange={() => handleFilterChange(col, val)}
                  /> {val || "(boş)"}
                </label>
              ))}
          </div>
        )
      ))}
    </div>
  );
};

export default FaturaListesi;
